import React, { useCallback, useEffect, useRef } from "react";
import clsx from "clsx";

import {
  displayOtpDigit,
  getFirstEmptyOtpIndex,
  slotsToOtpValue,
  toOtpSlots,
} from "./otp-input.utils.ts";

import styles from "./otp-input.module.css";

export type OtpInputProps = {
  id: string;
  label?: string;
  required?: boolean;
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string;
};

export { normalizeOtpValue } from "./otp-input.utils.ts";

export function OtpInput({
  id,
  label,
  required = false,
  length = 6,
  value,
  onChange,
  onBlur,
  disabled = false,
  error,
}: OtpInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const errorId = `${id}-error`;
  const hasError = error != null && error !== "";

  const focusInput = useCallback(
    (index: number) => {
      if (index < 0 || index >= length) return;
      const input = inputRefs.current[index];
      input?.focus();
      input?.select();
    },
    [length],
  );

  useEffect(() => {
    focusInput(getFirstEmptyOtpIndex(value, length));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- auto-focus on mount only

  const setInputRef = useCallback(
    (index: number) => (node: HTMLInputElement | null) => {
      inputRefs.current[index] = node;
    },
    [],
  );

  const applySlots = useCallback(
    (slots: string[], focusIndex: number) => {
      onChange(slotsToOtpValue(slots));
      focusInput(focusIndex);
    },
    [focusInput, onChange],
  );

  const handleDigitChange = useCallback(
    (index: number, rawValue: string) => {
      const digits = rawValue.replace(/\D/g, "");
      if (!digits) return;

      const slots = toOtpSlots(value, length);

      if (digits.length > 1) {
        let j = index;
        for (const d of digits) {
          if (j >= length) break;
          slots[j] = d;
          j += 1;
        }
        applySlots(slots, Math.min(j, length - 1));
        return;
      }

      slots[index] = digits.slice(-1);
      applySlots(slots, index < length - 1 ? index + 1 : index);
    },
    [applySlots, length, value],
  );

  const handleKeyDown = useCallback(
    (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Backspace") {
        event.preventDefault();
        const slots = toOtpSlots(value, length);
        if (/\d/.test(value[index] ?? "")) {
          slots[index] = "";
          applySlots(slots, index);
          return;
        }
        if (index > 0) {
          slots[index - 1] = "";
          applySlots(slots, index - 1);
        }
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        focusInput(index - 1);
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        focusInput(index + 1);
      }
    },
    [applySlots, focusInput, length, value],
  );

  const handlePaste = useCallback(
    (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const pasted = event.clipboardData.getData("text").replace(/\D/g, "");
      if (!pasted) return;

      const slots = toOtpSlots(value, length);
      let j = index;
      for (const d of pasted) {
        if (j >= length) break;
        slots[j] = d;
        j += 1;
      }
      applySlots(slots, Math.min(j, length - 1));
    },
    [applySlots, length, value],
  );

  return (
    <div className={styles.wrapper}>
      {label ? (
        <span
          className={clsx("sub-heading-md", styles.label)}
          id={`${id}-label`}
        >
          {label}
          {required ? (
            <span className="text-danger text-nowrap" aria-hidden>
              {"\u00A0*"}
            </span>
          ) : null}
        </span>
      ) : null}
      <div
        className={styles.group}
        role="group"
        aria-label={label}
        aria-invalid={hasError ? true : undefined}
        aria-describedby={hasError ? errorId : undefined}
      >
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            ref={setInputRef(index)}
            id={index === 0 ? id : `${id}-${index}`}
            type="text"
            inputMode={index === 0 ? "numeric" : undefined}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            className={clsx(styles.cell, "body-sm")}
            value={displayOtpDigit(value, index)}
            disabled={disabled}
            maxLength={1}
            aria-label={
              label
                ? `${label} digit ${index + 1} of ${length}`
                : `Digit ${index + 1} of ${length}`
            }
            onChange={(event) => handleDigitChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={(event) => handlePaste(index, event)}
            onFocus={(event) => event.currentTarget.select()}
            onBlur={onBlur}
          />
        ))}
      </div>
      {hasError ? (
        <p
          id={errorId}
          className={clsx(styles.message, styles.messageError)}
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
